/**
 * Billing attention banners (TMMRSL-0046, NOTIF-002/003): persistent alerts saying what is wrong
 * with the reseller's billing and what to do — open charges (and payouts on hold), no card around
 * official launch, an expired card. Zero to three show, in that order; none when nothing is wrong.
 * It loads the card, the charges and the standing itself. A failed load contributes nothing (the
 * Billing page's sections show the errors), and nothing renders while any load is pending, so the
 * page never flashes. `showLink` adds an "Open billing" link to `/billing` (the Dashboard).
 */
import type { ReactNode } from 'react';
import { Alert, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resellerAdminApi } from '@/api/resellerAdmin';
import { resellerBillingApi } from '@/api/resellerBilling';
import { useRemote } from '@/components/useRemote';
import { formatCivilOrDash } from '@/i18n/format';
import { todayCivil } from '@/i18n/periods';
import { useLocale } from '@/i18n/useLocale';
import { isOpenCharge } from './openCharges';

const loadCard = () => resellerBillingApi.getCard();
const loadCharges = () => resellerBillingApi.listCharges();
const loadStanding = () => resellerAdminApi.getStanding();

interface AttentionAlert {
  key: string;
  severity: 'error' | 'warning';
  message: string;
}

export function BillingAttention({ showLink }: { showLink: boolean }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const card = useRemote(loadCard);
  const charges = useRemote(loadCharges);
  const standing = useRemote(loadStanding);

  if (card.status === 'loading' || charges.status === 'loading' || standing.status === 'loading') {
    return null;
  }

  const alerts: AttentionAlert[] = [];

  if (charges.status === 'ready' && charges.data) {
    const open = charges.data.filter(isOpenCharge);
    if (open.length > 0) {
      const held = open.some((charge) => charge.holdsPayouts);
      alerts.push({
        key: 'charges',
        severity: 'error',
        message:
          t('resellerBilling.attention.charges', { count: open.length }) +
          (held ? ` ${t('resellerBilling.attention.payoutsHeld')}` : ''),
      });
    }
  }

  const launchOn =
    standing.status === 'ready' ? standing.data?.officialLaunchOn?.slice(0, 10) : null;
  if (card.status === 'ready' && card.data === null && launchOn) {
    // Civil dates compare as strings; "today" is the local calendar's, as on the Agreement page.
    if (launchOn > todayCivil()) {
      alerts.push({
        key: 'launch',
        severity: 'warning',
        message: t('resellerBilling.attention.launchNoCard', {
          date: formatCivilOrDash(launchOn, locale, t('statements.unavailable')),
        }),
      });
    } else {
      alerts.push({
        key: 'noCard',
        severity: 'error',
        message: t('resellerBilling.attention.noCard'),
      });
    }
  }

  if (card.status === 'ready' && card.data?.status === 'EXPIRED') {
    alerts.push({
      key: 'expired',
      severity: 'warning',
      message: t('resellerBilling.attention.expired'),
    });
  }

  if (alerts.length === 0) return null;

  const link: ReactNode = showLink ? (
    <Button component={RouterLink} to="/billing" color="inherit" size="small">
      {t('resellerBilling.attention.open')}
    </Button>
  ) : undefined;

  return (
    <Stack spacing={1.5}>
      {alerts.map((alert) => (
        <Alert key={alert.key} severity={alert.severity} action={link}>
          {alert.message}
        </Alert>
      ))}
    </Stack>
  );
}
